/**
 * Minimal history router: /, /d/YYYY-MM-DD, /n/:id, /settings, with `?b=<block id>` to land on a
 * block inside its day or page (SPEC §7.1). Back and Forward just work.
 */
import { useSyncExternalStore } from 'react'

export type Route =
  | { readonly name: 'journal'; readonly date: string | null; readonly block: string | null }
  | { readonly name: 'node'; readonly id: string; readonly block: string | null }
  | { readonly name: 'settings' }

const UUID = /^[0-9a-f-]{36}$/

export function parse(url: string): Route {
  const [path, query = ''] = url.split('?') as [string, string?]
  const b = new URLSearchParams(query).get('b')
  const block = b && UUID.test(b) ? b : null
  const d = /^\/d\/(\d{4}-\d{2}-\d{2})$/.exec(path)
  if (d) return { name: 'journal', date: d[1]!, block }
  const n = /^\/n\/([0-9a-f-]{36})$/.exec(path)
  if (n) return { name: 'node', id: n[1]!, block }
  if (path === '/settings') return { name: 'settings' }
  return { name: 'journal', date: null, block }
}

const listeners = new Set<() => void>()
const here = (): string => location.pathname + location.search
/**
 * The route is the last navigation, not the address bar: the journal rewrites the address while
 * you scroll (replaceQuietly), and that must not re-route anything.
 */
let current = typeof location !== 'undefined' ? here() : '/'
if (typeof window !== 'undefined') {
  addEventListener('popstate', () => {
    current = here()
    for (const f of [...listeners]) f()
  })
}

export function navigate(url: string): void {
  if (url !== here()) history.pushState(null, '', url)
  // Same place again (e.g. landing on the same block twice) still tells the views. A copy: a
  // listener that subscribes anew while we notify must not be called in the same round.
  current = url
  for (const f of [...listeners]) f()
}

/** Change the URL without a navigation (the journal follows the day in view, SPEC §7.3). */
export function replaceQuietly(url: string): void {
  if (url !== here()) history.replaceState(history.state, '', url)
}

/** Subscribe to navigations (for recents). */
export function onNavigate(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

let navSeq = 0
listeners.add(() => navSeq++)

/** Stable subscribe/snapshot functions: React re-subscribes whenever their identity changes. */
const subscribe = (cb: () => void): (() => void) => {
  listeners.add(cb)
  return () => listeners.delete(cb)
}
const getNavSeq = (): number => navSeq
const getUrl = (): string => current

/** Incremented on every navigation, even to the same URL. */
export function useNavSeq(): number {
  return useSyncExternalStore(subscribe, getNavSeq, getNavSeq)
}

export function useRoute(): Route {
  return parse(useSyncExternalStore(subscribe, getUrl, getUrl))
}

/** Where a block lives: its day (journal) or its page, with `?b=` to land on it. */
export function hrefToBlock(day: string | null, pageId: string | null, block: string): string {
  if (day) return `/d/${day}?b=${block}`
  return `/n/${pageId}?b=${block}`
}
